import { Box, Tabs as MuiTabs, Tab } from "@mui/material";
import React, { useRef } from "react";

import type { Widget as WidgetType } from "@/lib/api";
import Widget from "@/features/dashboard/components/widget";
import { WidgetContentContext } from "@/features/dashboard/components/widget-container-card";

const TabPanel = ({
  children,
  activeTab,
  index,
  ...other
}: {
  children: React.ReactNode;
  activeTab: number;
  index: number;
}) => {
  return (
    <Box
      role="tabpanel"
      hidden={activeTab !== index}
      aria-labelledby={`tab-${index}`}
      style={{
        flexGrow: 1,
        height: "100%",
      }}
      {...other}
    >
      {activeTab === index && <>{children}</>}
    </Box>
  );
};

const Tabs = ({
  tabs,
  activeTab,
  onTabChange,
}: {
  tabs: WidgetType[];
  activeTab: number;
  onTabChange: (index: number) => void;
}) => {
  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    onTabChange(newValue);
  };

  return (
    <MuiTabs
      variant="scrollable"
      scrollButtons="auto"
      value={activeTab}
      onChange={handleTabChange}
    >
      {tabs.map((tab, i) => (
        <Tab key={tab.id} label={tab.title} value={i} />
      ))}
    </MuiTabs>
  );
};

const Panels = ({ tabs, activeTab }: { tabs: WidgetType[]; activeTab: number }) => {
  return (
    <>
      {tabs.map((tab, i) => (
        <TabPanel key={tab.id} activeTab={activeTab} index={i}>
          <Widget widget={tab} />
        </TabPanel>
      ))}
    </>
  );
};

type Props = {
  tabs: WidgetType[];
  activeTab: number;
  onTabChange: (index: number) => void;
  showTabs?: boolean;
};

const WidgetTabs = ({ tabs, activeTab, onTabChange, showTabs = true }: Props) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const hasTabs = tabs.length > 1;

  if (!hasTabs) return null;

  return (
    <WidgetContentContext.Provider
      value={{
        ref: contentRef,
      }}
    >
      {showTabs && <Tabs tabs={tabs} activeTab={activeTab} onTabChange={onTabChange} />}
      <div
        style={{
          flexGrow: 1,
          maxHeight: showTabs ? "calc(100% - 48px)" : "100%",
        }}
        ref={contentRef}
      >
        <Panels activeTab={activeTab} tabs={tabs} />
      </div>
    </WidgetContentContext.Provider>
  );
};

export default WidgetTabs;
